import { AttachmentChip } from '@ordo/ui/attachment-chip'
import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { RecordSheet } from '@ordo/ui/record-sheet'
import { type Column, DataGrid } from 'compositions/DataGrid'
import { Page } from 'compositions/Page'
import { type CapturedComm, useCapturedComm, useCapturedComms } from 'hooks/api'
import { Mail, MessageSquare } from 'lucide-react'
import { useState } from 'react'

/**
 * Preview-only admin page: lists outbound comms captured by SendGrid
 * sandbox / Twilio test mode and lets the operator open one to view the
 * rendered subject + body. The route is mounted only on deployments
 * where the server reports `ENVIRONMENT=preview`; outside preview the
 * server's `/api/captured-comms/*` route 404s and this page surfaces
 * "no captured comms" naturally.
 */
export function AdminCapturedComms() {
  const { data, loading } = useCapturedComms({ take: 100 })
  const [openId, setOpenId] = useState<string | null>(null)

  const columns: Column<CapturedComm>[] = [
    {
      key: 'channel',
      header: 'Channel',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.channel === 'email' ? (
            <Mail className="h-4 w-4 text-muted-foreground" aria-hidden />
          ) : (
            <MessageSquare className="h-4 w-4 text-muted-foreground" aria-hidden />
          )}
          <span className="capitalize">{row.channel}</span>
        </div>
      ),
    },
    {
      key: 'toAddress',
      header: 'Recipient',
      sortable: true,
      filterable: true,
      render: (row) => <span>{row.toAddress}</span>,
    },
    {
      key: 'subject',
      header: 'Subject / preview',
      mobile: 'title',
      render: (row) => (
        <span className="text-muted-foreground">
          {row.subject ?? row.body.slice(0, 80) + (row.body.length > 80 ? '…' : '')}
        </span>
      ),
    },
    {
      key: 'providerStatus',
      header: 'Status',
      mobile: 'aside',
      sortable: true,
      render: (row) =>
        row.providerStatus === 'validated' ? (
          <Badge variant="default">Validated</Badge>
        ) : (
          <Badge variant="secondary" className="bg-red-500/15 text-red-600 dark:text-red-400">
            Rejected
          </Badge>
        ),
    },
    {
      key: 'capturedAt',
      header: 'Captured',
      sortable: true,
      render: (row) => new Date(row.capturedAt).toLocaleString(),
    },
  ]

  return (
    <Page>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold">Captured comms</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Outbound emails and SMS captured while running in preview mode. The provider validated each request but no
            delivery occurred.
          </p>
        </div>
      </div>

      <DataGrid
        columns={columns}
        data={data?.rows ?? []}
        keyField="id"
        pageSize={20}
        loading={loading}
        storageKey="admin-captured-comms"
        onRowClick={(row) => setOpenId(row.id)}
      />

      <CapturedCommDetailSheet id={openId} onClose={() => setOpenId(null)} />
    </Page>
  )
}

function CapturedCommDetailSheet({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { data: row } = useCapturedComm(id)
  const open = id !== null

  return (
    <RecordSheet
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose()
        }
      }}
      title={row?.subject ?? (row ? `${row.channel} to ${row.toAddress}` : 'Captured comm')}
      description={row ? `${row.fromAddress ?? 'unknown sender'} → ${row.toAddress}` : undefined}
      meta={
        row ? (
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="capitalize">
              {row.channel}
            </Badge>
            {row.providerStatus === 'validated' ? (
              <Badge variant="default">Validated</Badge>
            ) : (
              <Badge variant="secondary" className="bg-red-500/15 text-red-600 dark:text-red-400">
                Rejected
              </Badge>
            )}
            {row.providerRef ? <span className="text-xs text-muted-foreground">{row.providerRef}</span> : null}
          </div>
        ) : null
      }
      size="xl"
      footerActions={
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      }>
      {row ? (
        <div className="space-y-6">
          {row.providerError ? (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm">
              <p className="font-medium text-red-500">Provider rejected the request</p>
              <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{row.providerError}</p>
            </div>
          ) : null}

          <section>
            <h3 className="mb-2 text-sm font-medium">Rendered body</h3>
            {row.channel === 'email' ? (
              <iframe
                title="Captured email body"
                srcDoc={row.body}
                sandbox=""
                className="h-[600px] w-full rounded-md border bg-background"
              />
            ) : (
              <pre className="whitespace-pre-wrap rounded-md border bg-muted p-3 text-sm">{row.body}</pre>
            )}
          </section>

          <CapturedAttachments row={row} />

          {Object.keys(row.metadata).length > 0 ? (
            <section>
              <h3 className="mb-2 text-sm font-medium">Metadata</h3>
              <pre className="overflow-x-auto rounded-md border bg-muted p-3 text-xs">
                {JSON.stringify(row.metadata, null, 2)}
              </pre>
            </section>
          ) : null}
        </div>
      ) : (
        <div className="py-12 text-center text-muted-foreground">Loading…</div>
      )}
    </RecordSheet>
  )
}

interface CapturedAttachmentMeta {
  filename: string
  type: string
  /** Length of the base64 payload, not of the file. */
  sizeBytes: number
}

/** Each attachment the email carried, downloadable from the send's stored file. */
function CapturedAttachments({ row }: { row: CapturedComm }) {
  const attachments = (row.metadata['attachments'] ?? []) as CapturedAttachmentMeta[]
  if (attachments.length === 0) {
    return null
  }
  return (
    <section>
      <h3 className="mb-2 text-sm font-medium">Attachments</h3>
      <div className="flex flex-wrap gap-2">
        {attachments.map((attachment, index) => (
          <AttachmentChip
            key={`${attachment.filename}-${index}`}
            variant="persisted"
            attachment={{
              id: `${row.id}-${index}`,
              fileName: attachment.filename,
              fileUrl: `/api/captured-comms/${row.id}/attachments/${index}`,
              // ponytail: base64 → bytes, exact to within the 2 padding bytes
              fileSize: Math.floor((attachment.sizeBytes * 3) / 4),
              contentType: attachment.type,
            }}
          />
        ))}
      </div>
    </section>
  )
}
